/**
 * @file index.tsx
 *
 * @fileoverview Shows organization attachments when in a view mode.
 */
import { useIntl } from 'react-intl';
import { OrganizationWidgetsBoxLabel } from '~/jobiqo/components/Organization/Widgets/BoxLabel';
import { EditLink } from '~/jobiqo/components/Organization/Widgets/EditLink';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Box } from '~/vcl/atoms/07-box';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { LinkButton } from '~/vcl/atoms/01-forms/01-button/02-link-button';
import { JobPerFileIcon } from '~/vcl/icons/JobPerFile';

export interface OrganizationAttachmentsWidgetViewProps {
  /**
   * The current organization profile.
   */
  organization: OrganizationProfile;
  /**
   * The current box id.
   */
  boxId: string | undefined;
}

export const OrganizationAttachmentsWidgetView = ({
  organization,
  boxId,
}: OrganizationAttachmentsWidgetViewProps) => {
  const intl = useIntl();
  const { canEdit } = organization;

  if (!canEdit && organization?.attachments?.length == 0) {
    return null;
  }

  return (
    <Box
      label={
        <OrganizationWidgetsBoxLabel>
          {intl.formatMessage({
            id: 'organization_attachments_label',
            defaultMessage: 'Organization attachments',
          })}
        </OrganizationWidgetsBoxLabel>
      }
    >
      <FlexItems justifyContent="flex-end">
        <EditLink boxId={boxId} organization={organization} />
      </FlexItems>
      <ul>
        {organization.attachments?.map(
          (attachment) =>
            attachment?.url && (
              <li key={attachment.id}>
                <FlexItems flexWrap="nowrap" alignItems="center">
                  <FlexItem marginRight={4}>
                    <JobPerFileIcon color="secondary" title="Attachment" />
                  </FlexItem>
                  <FlexItem justifySelf="flex-start">
                    <LinkButton
                      raised={false}
                      buttonStyle="secondary"
                      rel="no-opener"
                      href={attachment.url}
                    >
                      <Text marginTop={0}>{attachment.fileName}</Text>
                    </LinkButton>
                  </FlexItem>
                </FlexItems>
              </li>
            ),
        )}
      </ul>
    </Box>
  );
};
